import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Bot, Brain, MessageSquare, X, FileText, GanttChart, Plus, Pencil, ThumbsUp, Zap, Users } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import GanttChartModal from '../components/GanttChartModal';
import ProjectCharterModal from '../components/ProjectCharterModal';
import MethodologyAgent from '../components/MethodologyAgent';
import AIAgentCreator from '../components/AIAgentCreator';
import AIAgentInteraction from '../components/AIAgentInteraction';
import MeetingSummarizerModal from '../components/MeetingSummarizerModal';
import MeetingAgendaPlanner from '../components/MeetingAgendaPlanner';
import CustomerMoodAnalyzer from '../components/CustomerMoodAnalyzer';
import ProductivityImprover from '../components/ProductivityImprover';
import TeamStandupAgent from '../components/TeamStandupAgent';

interface Agent {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  capabilities: string[];
  status: 'available' | 'coming-soon';
  is_custom?: boolean;
  instructions?: string;
  attachment_url?: string;
  attachment_name?: string;
  goals?: string;
}

const defaultAgents: Agent[] = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    name: 'Project Methodology Advisor',
    description: 'Get expert recommendations on the best project management methodology for your project',
    icon: <Brain className="h-8 w-8 text-purple-500" />,
    capabilities: [
      'Analyze project requirements',
      'Evaluate team structure',
      'Consider timeline constraints',
      'Assess stakeholder involvement',
      'Recommend suitable methodology'
    ],
    status: 'available'
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    name: 'Project Charter Generator',
    description: 'Generate comprehensive project charters',
    icon: <FileText className="h-8 w-8 text-green-500" />,
    capabilities: [
      'Project overview and objectives',
      'Stakeholder analysis',
      'Deliverables and milestones',
      'Risk assessment',
      'Resource allocation'
    ],
    status: 'available'
  },
  {
    id: '00000000-0000-0000-0000-000000000003',
    name: 'Gantt Chart Generator',
    description: 'Create visual project timelines and dependencies',
    icon: <GanttChart className="h-8 w-8 text-yellow-500" />,
    capabilities: [
      'Task breakdown and sequencing',
      'Timeline visualization',
      'Dependency mapping',
      'Resource allocation',
      'Milestone tracking'
    ],
    status: 'available'
  },
  {
    id: '00000000-0000-0000-0000-000000000004',
    name: 'Meeting Agenda Planner',
    description: 'Generate structured meeting agendas with discussion questions',
    icon: <MessageSquare className="h-8 w-8 text-blue-500" />,
    capabilities: [
      'Create detailed meeting agendas',
      'Generate discussion questions for each topic',
      'Allocate time for each agenda item',
      'Structure meetings for maximum productivity',
      'Export as text or PDF format'
    ],
    status: 'available'
  },
  {
    id: '00000000-0000-0000-0000-000000000005',
    name: 'Meeting Summarizer',
    description: 'Convert meeting notes into structured summaries',
    icon: <MessageSquare className="h-8 w-8 text-pink-500" />,
    capabilities: [
      'Extract key meeting details',
      'Identify action items and owners',
      'Structure meeting notes professionally',
      'Highlight important decisions',
      'Track meeting attendance'
    ],
    status: 'available'
  },
  {
    id: '00000000-0000-0000-0000-000000000006',
    name: 'Customer Mood Analyzer',
    description: 'Analyze client feedback to determine mood and provide recommendations',
    icon: <ThumbsUp className="h-8 w-8 text-emerald-500" />,
    capabilities: [
      'Detect client mood and sentiment',
      'Provide sentiment score from -100 to +100',
      'Identify key reasons for client mood',
      'Suggest actionable responses',
      'Recommend follow-up timeline'
    ],
    status: 'available'
  },
  {
    id: '00000000-0000-0000-0000-000000000007',
    name: 'Productivity Improvement Planner',
    description: 'Get a personalized productivity plan to overcome challenges and achieve your goals',
    icon: <Zap className="h-8 w-8 text-amber-500" />,
    capabilities: [
      'Analyze your productivity challenges',
      'Create a personalized daily schedule',
      'Develop focus and distraction management strategies',
      'Design habit-building systems',
      'Provide actionable improvement steps'
    ],
    status: 'available'
  },
  {
    id: '00000000-0000-0000-0000-000000000008',
    name: 'Team Standup Summarizer',
    description: 'Transform raw standup notes into structured summaries with actionable insights',
    icon: <Users className="h-8 w-8 text-teal-500" />,
    capabilities: [
      'Identify team members and their tasks',
      'Highlight completed work and current focus',
      'Identify blockers and challenges',
      'Assess team mood and energy levels',
      'Provide actionable follow-up suggestions'
    ],
    status: 'available'
  },
];

export default function AIAgents({ navigate }: { navigate: (to: string) => void }) {
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [agents, setAgents] = useState<Agent[]>(defaultAgents);
  const [selectedCustomAgent, setSelectedCustomAgent] = useState<Agent | null>(null);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);

  useEffect(() => {
    fetchCustomAgents();
  }, []);

  const fetchCustomAgents = async () => {
    try {
      const { data: customAgents, error } = await supabase
        .from('ai_agents')
        .select('*')
        .eq('is_custom', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (customAgents) {
        const formattedCustomAgents: Agent[] = customAgents.map(agent => ({
          id: agent.id,
          name: agent.name,
          description: agent.goals,
          goals: agent.goals,
          icon: <Bot className="h-8 w-8 text-purple-500" />,
          capabilities: extractCapabilitiesFromPrompt(agent.system_prompt) || [
            'Custom agent capabilities',
            'Personalized behavior',
            'Specialized knowledge',
            'Task-specific responses',
            'Contextual understanding'
          ],
          status: 'available',
          is_custom: true,
          instructions: agent.instructions,
          attachment_url: agent.attachment_url,
          attachment_name: agent.attachment_name
        }));

        setAgents([...defaultAgents, ...formattedCustomAgents]);
      }
    } catch (error) {
      console.error('Error fetching custom agents:', error);
      toast.error('Failed to load custom agents');
    }
  };

  // Function to extract capabilities from system prompt
  const extractCapabilitiesFromPrompt = (prompt: string, maxPoints: number = 5): string[] => {
    if (!prompt) return [];
    
    // Split by common delimiters and get sentences or bullet points
    const lines = prompt.split(/\n|\.|\•/).filter(line => line.trim().length > 0);
    
    // Take only the first few points
    return lines.slice(0, maxPoints).map(line => {
      // Clean up the line
      return line.trim()
        .replace(/^[-•*]\s*/, '') // Remove bullet markers
        .replace(/^[0-9]+\.\s*/, '') // Remove numbered list markers
        .replace(/^I\s+(can|will)\s+/, '') // Remove "I can" or "I will"
        .replace(/^(The\s+agent\s+(can|will)\s+)/, '') // Remove "The agent can/will"
        .charAt(0).toUpperCase() + line.trim().slice(1); // Capitalize first letter
    });
  };

  const handleDeleteAgent = async (agentId: string) => {
    try {
      const { error } = await supabase
        .from('ai_agents')
        .delete()
        .eq('id', agentId);

      if (error) throw error;

      setAgents(agents.filter(agent => agent.id !== agentId));
      toast.success('Agent deleted successfully');
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Failed to delete agent');
    }
  };

  const handleAgentClick = (agent: Agent) => {
    if (agent.is_custom) {
      setSelectedCustomAgent(agent);
    } else if (agent.status === 'available') {
      setSelectedAgent(agent.id);
    }
  };

  const handleEditClick = (agent: Agent, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the agent click
    setEditingAgent(agent);
    setShowCreateForm(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Agents</h1>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              Discover our specialized AI agents designed to enhance your project management workflow. To use AI features, please add your OpenAI API key in Settings.
            </p>
            <button
              onClick={() => {
                setEditingAgent(null);
                setShowCreateForm(true);
              }}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Custom Agent
            </button>
          </div>

          {showCreateForm ? (
            <AIAgentCreator
              onSuccess={() => {
                setShowCreateForm(false);
                setEditingAgent(null);
                fetchCustomAgents();
              }}
              onCancel={() => {
                setShowCreateForm(false);
                setEditingAgent(null);
              }}
              initialData={editingAgent || undefined}
            />
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border-2 border-transparent hover:border-indigo-500 transition-all duration-300"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        {agent.icon}
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        agent.status === 'available'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
                      }`}>
                        {agent.status === 'available' ? 'Available' : 'Coming Soon'}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {agent.name}
                    </h3>
                    
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {agent.description}
                    </p>

                    <div className="space-y-2">
                      {agent.capabilities.map((capability, index) => (
                        <div
                          key={index}
                          className="flex items-center text-sm text-gray-600 dark:text-gray-300"
                        >
                          <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                          {capability}
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 flex justify-between items-center">
                      <button
                        disabled={agent.status === 'coming-soon'}
                        onClick={() => handleAgentClick(agent)}
                        className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                          agent.status === 'available'
                            ? 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                            : 'bg-gray-400 cursor-not-allowed'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                      >
                        {agent.status === 'available' ? 'Use Agent' : 'Coming Soon'}
                      </button>
                      {agent.is_custom && (
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => handleEditClick(agent, e)}
                            className="p-2 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100"
                            title="Edit agent"
                          >
                            <Pencil className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteAgent(agent.id)}
                            className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100"
                            title="Delete agent"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selectedAgent === 'meeting-summarizer' && (
          <MeetingSummarizerModal 
            onClose={() => setSelectedAgent(null)}
            navigate={navigate}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000005' && (
          <MeetingSummarizerModal 
            onClose={() => setSelectedAgent(null)}
            navigate={navigate}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000002' && (
          <ProjectCharterModal
            onClose={() => setSelectedAgent(null)}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000001' && (
          <MethodologyAgent
            onClose={() => setSelectedAgent(null)}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000004' && (
          <MeetingAgendaPlanner
            onClose={() => setSelectedAgent(null)}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000006' && (
          <CustomerMoodAnalyzer
            onClose={() => setSelectedAgent(null)}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000007' && (
          <ProductivityImprover
            onClose={() => setSelectedAgent(null)}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000008' && (
          <TeamStandupAgent
            onClose={() => setSelectedAgent(null)}
          />
        )}
        {selectedAgent === '00000000-0000-0000-0000-000000000003' && (
          <GanttChartModal
            onClose={() => setSelectedAgent(null)}
          />
        )}
        {selectedCustomAgent && (
          <AIAgentInteraction
            agent={selectedCustomAgent}
            onClose={() => setSelectedCustomAgent(null)}
          />
        )}
      </main>
    </div>
  );
}